import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ErrorIcon } from '@/assets/icons/error';
import { Button } from '@/components/ButtonComponent';
import { ModalPullDownIndicator } from '@/components/PullDownIndicator';
import { Screen } from '@/components/ScreenComponent';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { productSwitcherStyles } from '@/feature/product-transition/components/LobbyWrapper';
import {
    PRODUCT_CHIPS_HEIGHT,
    ProductSwitcherChips,
} from '@/feature/product-transition/components/ProductSwitcherChips';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { Product } from '@/hooks/use-jurisdiction';
import { useProductSwitchModal } from '@/hooks/use-product-switch';
import { common, designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

const CONTENT_FADE_IN_DURATION = 300;

const styles = StyleSheet.create({
    content: {
        paddingHorizontal: 20,
    },
    footer: {
        marginBottom: 40,
    },
});

type Variant = 'unavailable' | 'pending';

const UnavailableContent = ({ variant }: { variant: Variant }) => {
    const { t } = useTranslation('prediction_access');
    const { availableProducts } = useAvailableProducts();

    const isPending = variant === 'pending';
    const fallback = availableProducts.find(p => p !== Product.Prediction);

    return (
        <Animated.View
            entering={FadeIn.duration(CONTENT_FADE_IN_DURATION)}
            style={[common.flex, common.justifySpaceBetween]}
        >
            <View style={[common.flex, common.justifyCenter, common.alignCenter, styles.content]}>
                <ErrorIcon />
                <SizedBox value={16} />
                <Text variant="headlineMedium" fontWeight="bold" lineHeight={32} textAlign="center">
                    {t(isPending ? 'pending_title' : 'unavailable_title')}
                </Text>
                <SizedBox value={12} />
                <Text
                    variant="titleMedium"
                    fontWeight="400"
                    color={ds.colorToken.text.secondary}
                    textAlign="center"
                    lineHeight={24}
                >
                    {t(isPending ? 'pending_message' : 'unavailable_message')}
                </Text>
            </View>

            {fallback ? (
                <View style={[styles.footer, common.paddingHorizontal]}>
                    <Box mb="s16">
                        <Button
                            hierarchy="primary"
                            label={t('unavailable_cta')}
                            onPress={() => useProductTransitionStore.getState().actions.triggerTransition(fallback)}
                        />
                    </Box>
                </View>
            ) : null}
        </Animated.View>
    );
};

/**
 * Shown when Predictions is unavailable to the user (blocked / failed / in-review). Offers a way
 * back to another product when one is available.
 */
export const PredictionUnavailableScreen = ({ variant = 'unavailable' }: { variant?: Variant }) => {
    const insets = useSafeAreaInsets();
    const { switchingAvailable } = useProductSwitchModal();

    if (!switchingAvailable) {
        return (
            <Screen edges={['top', 'bottom']}>
                <UnavailableContent variant={variant} />
            </Screen>
        );
    }

    return (
        <Box flex={1} backgroundColor={ds.colorToken.background.brand.strong} style={{ paddingTop: insets.top }}>
            <Box height={PRODUCT_CHIPS_HEIGHT}>
                <MaxWidthWrapper>
                    {/* Address the misalign gear */}
                    <Box>
                        <ProductSwitcherChips />
                    </Box>
                </MaxWidthWrapper>
            </Box>
            <Box flex={1} style={isWeb ? undefined : productSwitcherStyles.lobbySheetShadow}>
                <Box
                    flex={1}
                    backgroundColor={ds.colorToken.background.neutral.base}
                    style={[productSwitcherStyles.borderRadius, { paddingBottom: insets.bottom }]}
                >
                    <Box top={12} width={'100%'}>
                        <ModalPullDownIndicator color={designSystem.colors.neutral500} />
                    </Box>
                    <UnavailableContent variant={variant} />
                </Box>
            </Box>
        </Box>
    );
};
